import { useCallback, useEffect, useState } from 'react';
import type { DayView, EntryRow, SectionName } from '../lib/day';
import { advanceTotal, dayApi, section, sectionEditable } from '../lib/day';
import { api, ApiError } from '../lib/api';
import type { Session } from '../lib/session';
import { can, isOwner as sessionIsOwner } from '../lib/session';
import { formatDate, formatLitres, formatRs } from '../lib/money';
import Timesheet from './Timesheet';
import Diesel from './Diesel';
import Misc from './Misc';
import SignaturePad from '../components/SignaturePad';
import PayRun from './PayRun';
import { onSynced } from '../lib/sync';
import type { CostCode } from '../lib/costcodes';

/**
 * The clerk's day — whichever of the three sections is on screen — plus the day
 * navigation, the lock banners and the submit button.
 *
 * Day navigation: ‹ Day before · the date · Day after › (never into the future)
 * · Back to today. The Approvals screen has none of this; the owner opens on the
 * oldest day still waiting.
 */

interface Reference {
  plants: { id: string; code: string; name: string; cost_code_id?: string | null }[];
  miscCategories: { id: string; name: string; requires_plant: number }[];
  costCodes: CostCode[];
}

interface Props {
  session: Session;
  tab: 'labour' | 'diesel' | 'misc';
  today: string;
  reference: Reference;
}

export default function Day({ session, tab, today, reference }: Props) {
  const [date, setDate] = useState(today);
  const [view, setView] = useState<DayView | null>(null);
  const [error, setError] = useState('');
  const [signing, setSigning] = useState<EntryRow | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [unsignedPrompt, setUnsignedPrompt] = useState<{ count: number; canOverride: boolean } | null>(null);
  // "The clerk prepares a pay run inside her Labour tab"; the owner has his own.
  const [labourSection, setLabourSection] = useState<'sheet' | 'pay'>('sheet');

  const owner = sessionIsOwner(session);

  const load = useCallback(async () => {
    try {
      const result = await dayApi.get(date);
      setView(result);
      setError('');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load the day.');
    }
  }, [date]);

  useEffect(() => { void load(); }, [load]);
  // The outbox drained: the Worker's figures have replaced the queued ones.
  useEffect(() => onSynced(() => void load()), [load]);

  // Keep the signature popup pointed at fresh data after a save.
  useEffect(() => {
    if (!signing || !view) return;
    const fresh = view.entries.find((entry) => entry.workerId === signing.workerId);
    if (fresh && fresh !== signing) setSigning(fresh);
  }, [view, signing]);

  if (!view) {
    return <div className="empty">{error || 'Loading…'}</div>;
  }

  const sectionName: SectionName = tab;
  const row = section(view, sectionName);
  const editable = sectionEditable(view, sectionName, owner);

  async function submit(overrideUnsigned = false) {
    setSubmitting(true);
    setError('');
    setUnsignedPrompt(null);
    try {
      await dayApi.submit(date, sectionName, overrideUnsigned);
      await load();
    } catch (err) {
      if (err instanceof ApiError && typeof err.detail?.unsignedAdvances === 'number') {
        setUnsignedPrompt({
          count: err.detail.unsignedAdvances,
          canOverride: err.detail.canOverride === true,
        });
      } else {
        setError(err instanceof ApiError ? err.message : 'Could not send that to the owner.');
      }
    } finally {
      setSubmitting(false);
    }
  }

  async function pullBack() {
    setSubmitting(true);
    try {
      await dayApi.pullBack(date, sectionName);
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not pull that back.');
    } finally {
      setSubmitting(false);
    }
  }

  const entered = view.entries.filter((entry) => entry.attendance !== null).length;
  const finalised = view.entries.filter((entry) => entry.finalised).length;
  const total = view.entries.length;

  return (
    <>
      <DayNav date={date} today={today} onDate={setDate} />

      {/* Entered with no signal. The figures a queued row shows are the last
          the Worker computed; the real ones arrive when it has seen the day. */}
      {(view.queued ?? 0) > 0 && (
        <div className="banner b-warn">
          {view.synthesised
            ? `This day has not reached the owner yet — ${view.queued} ${view.queued === 1 ? 'change is' : 'changes are'} waiting for a signal. Pay is worked out when they are sent.`
            : `${view.queued} ${view.queued === 1 ? 'change on this day is' : 'changes on this day are'} waiting for a signal. Pay is worked out when they are sent.`}
        </div>
      )}

      {/* Lock and gate banners, in the order the clerk needs them. */}
      {view.day.closedAt && (
        <div className="banner b-ok">
          Day approved and closed by the owner. Entries are locked.
        </div>
      )}

      {!view.day.closedAt && row.status === 'submitted' && !owner && (
        <div className="banner b-warn" style={{ justifyContent: 'space-between' }}>
          <span>{labelOf(sectionName)} submitted to the owner — locked for editing.</span>
          <button type="button" className="btn sm" disabled={submitting} onClick={() => void pullBack()}>
            Pull back and edit
          </button>
        </div>
      )}

      {!view.day.closedAt && row.status === 'approved' && (
        <div className="banner b-ok">{labelOf(sectionName)} approved by the owner.</div>
      )}

      {/* The gate is on sending, not entering: she can carry on working. */}
      {!view.day.closedAt && view.blockingDay && (
        <div className="banner b-warn" style={{ justifyContent: 'space-between' }}>
          <span>
            {formatDate(view.blockingDay)} is still waiting for the owner. You can carry on
            entering this day, but it cannot be sent until that one is approved.
          </span>
          <button
            type="button"
            className="btn sm"
            onClick={() => setDate(view.blockingDay!)}
          >
            Go to that day
          </button>
        </div>
      )}

      {error && <div className="banner b-no">{error}</div>}

      {/* "Labour tab (clerk) — two segments: Time sheet, Pay run." Only for a
          clerk the owner has trusted with pay runs; he has his own tab. */}
      {tab === 'labour' && !owner && can(session, 'payrun') && (
        <div className="seg wide">
          <button
            type="button"
            className={labourSection === 'sheet' ? 'on' : ''}
            onClick={() => setLabourSection('sheet')}
          >
            Time sheet<span className="cnt">{entered}/{total}</span>
          </button>
          <button
            type="button"
            className={labourSection === 'pay' ? 'on' : ''}
            onClick={() => setLabourSection('pay')}
          >
            Pay run
          </button>
        </div>
      )}

      {tab === 'labour' && !owner && labourSection === 'pay' ? (
        <PayRun isOwner={false} userName={session.user.name} userId={session.user.id} today={today} />
      ) : (
      <div className="withside">
        <div>
          {tab === 'labour' && (
            <div className="card">
              <div className="row" style={{ paddingTop: 0 }}>
                <div>
                  <h3>
                    Time sheet — {formatDate(date)}
                    {view.day.isSunday && <span className="pill p-wait" style={{ marginLeft: 8 }}>Sunday</span>}
                  </h3>
                  <div className="sub">
                    Open each person, confirm and save · {entered} of {total} done ·{' '}
                    {formatRs(view.totals.labourRs)}
                  </div>
                </div>
              </div>

              {!owner && editable && (
                <div
                  style={{
                    height: 6, background: 'var(--line)', borderRadius: 999,
                    overflow: 'hidden', margin: '0 0 10px',
                  }}
                >
                  <div
                    style={{
                      height: '100%',
                      width: `${total ? Math.round((entered / total) * 100) : 0}%`,
                      background: 'var(--text-success)',
                    }}
                  />
                </div>
              )}

              <Timesheet
                view={view}
                isOwner={owner}
                canAdvance={can(session, 'advance')}
                costCodes={reference.costCodes}
                onChanged={() => void load()}
                onSignature={setSigning}
                onError={setError}
              />
            </div>
          )}

          {tab === 'diesel' && (
            <Diesel
              view={view}
              plants={reference.plants}
              costCodes={reference.costCodes}
              isOwner={owner}
              canSetRate={can(session, 'rate')}
              onChanged={() => void load()}
              onError={setError}
            />
          )}

          {tab === 'misc' && (
            <Misc
              view={view}
              plants={reference.plants}
              categories={reference.miscCategories}
              costCodes={reference.costCodes}
              isOwner={owner}
              onChanged={() => void load()}
              onError={setError}
            />
          )}
        </div>

        <SidePanel view={view} />
      </div>
      )}

      {/* An unsigned advance blocks sending. The owner can let it through, and
          the override is recorded against his name. */}
      {unsignedPrompt && (
        <div className="banner b-warn" style={{ justifyContent: 'space-between' }}>
          <span>
            {unsignedPrompt.count} advance{unsignedPrompt.count === 1 ? '' : 's'} not signed for
            yet. Get the signature, or ask the owner to let it through.
          </span>
          {unsignedPrompt.canOverride && (
            <button
              type="button"
              className="btn sm dang"
              disabled={submitting}
              onClick={() => void submit(true)}
            >
              Send it anyway
            </button>
          )}
        </div>
      )}

      {!owner && editable && (
        <SubmitButton
          section={sectionName}
          total={total}
          entered={entered}
          finalised={finalised}
          busy={submitting}
          onSubmit={() => void submit()}
        />
      )}

      {signing && (
        <SignaturePad
          entry={signing}
          workDate={date}
          onClose={() => setSigning(null)}
          onSaved={() => void load()}
        />
      )}
    </>
  );
}

function labelOf(section: SectionName): string {
  return section === 'labour' ? 'Labour' : section === 'diesel' ? 'Diesel' : 'Misc expenses';
}

// ---------------------------------------------------------------- day nav

function DayNav({
  date, today, onDate,
}: {
  date: string;
  today: string;
  onDate: (date: string) => void;
}) {
  const shift = (days: number) =>
    new Date(Date.parse(`${date}T00:00:00Z`) + days * 86_400_000).toISOString().slice(0, 10);

  return (
    <div className="bar daynav" style={{ margin: '10px 0 0', alignItems: 'center' }}>
      <button
        type="button"
        className="btn sm"
        style={{ minWidth: 0 }}
        onClick={() => onDate(shift(-1))}
      >
        ‹ Day before
      </button>
      {/* The width lives in the stylesheet, not here: on a phone this takes a
          line of its own above the two buttons, and an inline flex would win
          over the rule that does it. */}
      <div className="daynav-date">
        {formatDate(date)}
        {date === today && ' · today'}
      </div>
      {/* Never into the future. */}
      <button
        type="button"
        className="btn sm"
        style={{ minWidth: 0 }}
        disabled={date >= today}
        onClick={() => onDate(shift(1))}
      >
        Day after ›
      </button>
      {date !== today && (
        <button
          type="button"
          className="btn sm"
          style={{ minWidth: 0 }}
          onClick={() => onDate(today)}
        >
          Back to today
        </button>
      )}
    </div>
  );
}

// ------------------------------------------------------------ submit button

function SubmitButton({
  section, total, entered, finalised, busy, onSubmit,
}: {
  section: SectionName;
  total: number;
  entered: number;
  finalised: number;
  busy: boolean;
  onSubmit: () => void;
}) {
  if (section !== 'labour') {
    return (
      <button
        type="button"
        className="btn pri"
        style={{ width: '100%', marginTop: 12 }}
        disabled={busy}
        onClick={onSubmit}
      >
        {section === 'diesel'
          ? 'Diesel done for the day — send to the owner'
          : 'Misc expenses done — send to the owner'}
      </button>
    );
  }

  if (entered < total) {
    return (
      <button type="button" className="btn" style={{ width: '100%' }} disabled>
        {total - entered} still to mark on site
      </button>
    );
  }
  if (finalised < total) {
    return (
      <button type="button" className="btn" style={{ width: '100%' }} disabled>
        {total - finalised} still to finalise
      </button>
    );
  }
  return (
    <button
      type="button"
      className="btn pri"
      style={{ width: '100%' }}
      disabled={busy}
      onClick={onSubmit}
    >
      Labour done for the day — send to the owner
    </button>
  );
}

// ------------------------------------------------------- running totals

/**
 * "A sticky running totals panel sits right of the timesheet showing labour
 * earned, diesel with litres, misc, advances, cash out, and the three section
 * states." Above 1180px only; the CSS hides it below that.
 *
 * Not gated on the `summary` permission: the day's own totals are part of doing
 * the job. `summary` covers period reporting.
 */
function SidePanel({ view }: { view: DayView }) {
  const stateOf = (name: SectionName) => {
    const row = section(view, name);
    if (row.status === 'approved') return <span className="pill p-ok">approved</span>;
    if (row.status === 'submitted') return <span className="pill p-info">sent</span>;
    return <span className="pill p-wait">open</span>;
  };

  return (
    <aside className="sidepanel">
      <div className="sub" style={{ margin: '0 0 8px' }}>Running today</div>

      <div className="metric">
        <div className="k">Labour earned</div>
        <div className="v" style={{ fontSize: 19 }}>{formatRs(view.totals.labourRs)}</div>
      </div>
      <div className="metric">
        <div className="k">Diesel</div>
        <div className="v" style={{ fontSize: 19 }}>{formatRs(view.totals.dieselRs)}</div>
        <div className="k">{formatLitres(view.totals.litresX100)} L</div>
      </div>
      <div className="metric">
        <div className="k">Misc</div>
        <div className="v" style={{ fontSize: 19 }}>{formatRs(view.totals.miscRs)}</div>
      </div>
      <div className="metric">
        <div className="k">Advances</div>
        <div className="v" style={{ fontSize: 19 }}>{formatRs(view.totals.advancesRs)}</div>
      </div>
      <div className="metric" style={{ background: 'var(--bg-warning)' }}>
        <div className="k" style={{ color: 'var(--text-warning)' }}>Cash out today</div>
        <div className="v" style={{ fontSize: 19, color: 'var(--text-warning)' }}>
          {formatRs(view.totals.cashOutRs)}
        </div>
      </div>

      <div className="sub" style={{ margin: '12px 0 2px' }}>Sections</div>
      {(['labour', 'diesel', 'misc'] as const).map((name) => (
        <div
          key={name}
          style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            fontSize: 12, padding: '4px 0',
          }}
        >
          <span className="sub">{labelOf(name)}</span>
          {stateOf(name)}
        </div>
      ))}
    </aside>
  );
}

export { api, advanceTotal };
